/* ==========================================================================
   CasMarkt v1.6 — quick view / quick add and the cart drawer's small moves.
   Owner items 9 and 10.

   Nothing here reimplements Spotlight behaviour. Quick view is Spotlight's
   own <quick-add-modal>, quick add is its <product-form>, and the drawer is
   still rendered and refreshed by cart-drawer.js. This file only:
     1. loads quick-add.js / product-form.js on pages whose sections did not
        ask for them (the collection template sets quick_add: "none"),
     2. flashes a success state on the Quick add pill,
     3. staggers the drawer's line items and marks the newest one.
   ========================================================================== */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- assets */
  function loadOnce(src, flag) {
    if (!src || document.querySelector('script[data-cm-src="' + flag + '"]')) return;
    var s = document.createElement('script');
    s.src = src;
    s.defer = true;
    s.setAttribute('data-cm-src', flag);
    document.head.appendChild(s);
  }

  /* This file is loaded from layout/theme.liquid, so it also runs on pages
     that never rendered a product card — the cart page, a policy page, the
     product page — where card-product.liquid never printed CMCardAssets. The
     quick view still has to be openable there (v2.1: the cart drawer's
     "Change" link), so the asset URLs are derived from this script's own src
     when the snippet has not supplied them. */
  var SELF_SRC = (function () {
    if (document.currentScript && document.currentScript.src) return document.currentScript.src;
    var scripts = document.getElementsByTagName('script');
    for (var i = scripts.length - 1; i >= 0; i -= 1) {
      if (scripts[i].src && scripts[i].src.indexOf('cm-cards.js') > -1) return scripts[i].src;
    }
    return '';
  })();

  function assetUrl(name) {
    return SELF_SRC ? SELF_SRC.replace(/cm-cards\.js(\?[^/]*)?$/, name) : '';
  }

  function assets() {
    var a = (window.CMCardAssets = window.CMCardAssets || {});
    if (!a.quickAdd) a.quickAdd = assetUrl('quick-add.js');
    if (!a.productForm) a.productForm = assetUrl('product-form.js');
    if (!a.quickAddCss) a.quickAddCss = assetUrl('quick-add.css');
    if (!a.quickViewCss || !a.quickViewCss.length) {
      a.quickViewCss = [assetUrl('section-main-product.css'), assetUrl('component-product-variant-picker.css')];
    }
    return a;
  }

  function ensureCardScripts() {
    var a = assets();

    // <modal-opener> comes from global.js and calls modal.show() the moment it
    // is clicked, so quick-add.js has to be in place before the first hover.
    if (document.querySelector('quick-add-modal') && !window.customElements.get('quick-add-modal')) {
      loadOnce(a.quickAdd, 'quick-add');
    }
    if (document.querySelector('product-form') && !window.customElements.get('product-form')) {
      loadOnce(a.productForm, 'product-form');
    }
  }

  /* ------------------------------------------------------ quick view: cache
     v2.0 item 3 — "it takes a second to load, make it fast".

     The panel's markup is the product page's <product-info>. Spotlight fetches
     the whole product page the moment the button is clicked, which is ~200 KB
     of HTML rendered on demand. This warms an in-memory Map on hover, focus
     and touchstart instead, so by the time the button is pressed the HTML is
     usually already in hand and quick-add.js can paint the panel synchronously.

     Two savings, not one:
       1. the request starts on hover instead of on click,
       2. once any product page has been parsed we know the product template's
          section id, so every later fetch asks for that section alone
          (~48 KB here instead of ~205 KB). The id is the same for every
          product, so it is learned once and kept in sessionStorage.

     This object is the single owner of the cache; quick-add.js only reads it.
     ------------------------------------------------------------------------ */
  var CMQuick = (function () {
    var LIMIT = 12;
    var SKEY = 'cm_pdp_section';
    var cache = new Map(); // url (no query) -> Promise<string|null>
    var ready = new Map(); // url -> resolved html, for a synchronous open
    var sectionId = null;

    try {
      sectionId = window.sessionStorage.getItem(SKEY) || null;
    } catch (e) {
      sectionId = null;
    }

    function remember(id) {
      if (!id || id === sectionId) return;
      sectionId = id;
      try {
        window.sessionStorage.setItem(SKEY, id);
      } catch (e) {
        /* private mode — the cache still works, it just re-learns each load */
      }
    }

    function forget() {
      sectionId = null;
      try {
        window.sessionStorage.removeItem(SKEY);
      } catch (e) {
        /* ignore */
      }
    }

    function extract(text) {
      var doc = new DOMParser().parseFromString(text, 'text/html');
      var el = doc.querySelector('product-info');
      if (!el) return null;
      remember(el.dataset.section);
      return el.outerHTML;
    }

    function request(url, useSection) {
      var target = url;
      if (useSection && sectionId) {
        // v2.2: a key can already carry ?variant=…, so the separator is not
        // always "?". This is the same URL shape product-info.js fetches when
        // the visitor picks an option on the product page.
        target += (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(sectionId);
      }
      return fetch(target, { credentials: 'same-origin' }).then(function (response) {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      });
    }

    function load(url) {
      var wanted = !!sectionId;
      return request(url, wanted)
        .then(extract)
        .then(function (html) {
          // A section id from an earlier theme version 200s but renders
          // nothing useful; fall back to the full page once and relearn.
          if (html || !wanted) return html;
          forget();
          return request(url, false).then(extract);
        })
        .catch(function () {
          if (!wanted) return null;
          forget();
          return request(url, false)
            .then(extract)
            .catch(function () {
              return null;
            });
        });
    }

    /* v2.2 — the cart drawer's "Change" link points at `line_item.url`, which
       is `/products/handle?variant=<id>`. That id is the whole point of the
       link: the panel has to open on the size and colour that are already in
       the bag, not on the product's default variant. So the variant parameter
       is part of the cache key and is passed straight through to Shopify,
       which renders the picker with those pills already checked — the same
       mechanism the product page uses, so no pill is clicked client-side and
       nothing re-fetches after the panel paints.

       Every other query parameter (utm_*, a collection path, …) renders the
       identical section, so it is dropped rather than fragmenting the cache. */
    function key(url) {
      var raw = (url || '').split('#')[0];
      if (!raw) return '';
      var parts = raw.split('?');
      if (!parts[1]) return parts[0];
      var variant = /(?:^|&)variant=(\d+)/.exec(parts[1]);
      return variant ? parts[0] + '?variant=' + variant[1] : parts[0];
    }

    return {
      /* Start (or reuse) the fetch. Always returns a promise. */
      get: function (url) {
        var k = key(url);
        if (!k) return Promise.resolve(null);
        var hit = cache.get(k);
        if (hit) return hit;

        var promise = load(k).then(function (html) {
          if (html) ready.set(k, html);
          else cache.delete(k);
          return html;
        });

        if (cache.size >= LIMIT) {
          var oldest = cache.keys().next().value;
          cache.delete(oldest);
          ready.delete(oldest);
        }
        cache.set(k, promise);
        return promise;
      },

      /* The already-resolved HTML, or null. Lets the panel open in the same
         task as the click instead of a microtask later. */
      peek: function (url) {
        return ready.get(key(url)) || null;
      },
    };
  })();

  window.CMQuick = CMQuick;

  /* Warm the cache from the card the pointer or the focus ring just reached.
     pointerover bubbles (pointerenter does not), and the Map lookup makes a
     repeat hit free, so one delegated listener covers every grid on the page
     including anything facets.js re-renders. */
  var lastIntent = null;
  var stylesWarmed = false;

  /* The product section's stylesheets, added once on the first sign of intent
     rather than on every listing page load, and long before the panel opens. */
  function warmStyles() {
    if (stylesWarmed) return;
    stylesWarmed = true;
    var a = assets();
    var hrefs = (a.quickViewCss || []).slice();
    // On a card-less page the panel's own stylesheet is missing too.
    if (a.quickAddCss) hrefs.push(a.quickAddCss);

    for (var i = 0; i < hrefs.length; i += 1) {
      if (!hrefs[i]) continue;
      // Compare filenames, not full URLs: the snippet's tags carry Shopify's
      // ?v= cache key and the derived ones do not.
      var file = hrefs[i].split('/').pop().split('?')[0];
      if (document.querySelector('link[href*="' + file + '"]')) continue;
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = hrefs[i];
      document.head.appendChild(link);
    }
  }

  function warm(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var scope = target.closest('.card-wrapper, .cm-vitem, .cm-actions');
    if (!scope || scope === lastIntent) return;
    lastIntent = scope;
    warmStyles();
    var opener = scope.querySelector('[data-product-url]');
    if (opener) CMQuick.get(opener.getAttribute('data-product-url'));
  }

  function watchIntent() {
    document.addEventListener('pointerover', warm, { passive: true });
    document.addEventListener('focusin', warm);
    document.addEventListener('touchstart', warm, { passive: true });
  }

  /* ------------------------------------------ quick view: the shared opener
     v2.1 — any element carrying `data-cm-qv-open` opens the quick-view panel
     for the product it names, from anywhere on the storefront. The cart
     drawer's "Change" link on a multi-variant line is the first caller (that
     markup belongs to the drawer, this behaviour belongs here), but nothing
     about the hook is drawer-specific: the product URL is read from the
     attribute itself, or from the element's href, or from data-product-url.

     Cards bring their own <quick-add-modal> per product. Everywhere else
     there is none, so one shared host is built on first use. It has to be born
     inside a .shopify-section: ModalDialog's connectedCallback reads
     `this.closest('.shopify-section').id` before moving itself to <body>.
     ------------------------------------------------------------------------ */
  var HOST_ID = 'CMQuickViewHost';
  var hostReady = null;

  var HOST_MARKUP =
    '<quick-add-modal id="' +
    HOST_ID +
    '" class="quick-add-modal cm-quickview">' +
    '<div role="dialog" aria-label="Choose product options" aria-modal="true" ' +
    'class="quick-add-modal__content global-settings-popup" tabindex="-1">' +
    '<div class="cm-qv__head">' +
    '<span class="cm-qv__eyebrow">Quick view</span>' +
    '<button id="ModalClose-cm-quickview-host" type="button" ' +
    'class="quick-add-modal__toggle cm-qv__close" aria-label="Close">' +
    '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close" viewBox="0 0 18 17" ' +
    'aria-hidden="true" focusable="false"><path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 ' +
    '7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 ' +
    '7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"/></svg>' +
    '</button>' +
    '</div>' +
    '<div id="QuickAddInfo-cm-quickview-host" class="quick-add-modal__content-info"></div>' +
    '<p class="cm-qv__note">Free US shipping &middot; ships from Texas</p>' +
    '</div>' +
    '</quick-add-modal>';

  function buildHost() {
    var existing = document.getElementById(HOST_ID);
    if (existing) return existing;
    var shell = document.createElement('div');
    shell.id = 'shopify-section-cm-quickview-host';
    shell.className = 'shopify-section';
    document.body.appendChild(shell);
    // insertAdjacentHTML parses the children first and upgrades the custom
    // element on insertion, so the constructor finds #QuickAddInfo-… already
    // in place — which document.createElement would not.
    shell.insertAdjacentHTML('beforeend', HOST_MARKUP);
    return document.getElementById(HOST_ID);
  }

  function ensureQuickView() {
    if (hostReady) return hostReady;
    var a = assets();
    warmStyles();
    if (!window.customElements.get('product-form')) loadOnce(a.productForm, 'product-form');
    if (!window.customElements.get('quick-add-modal')) loadOnce(a.quickAdd, 'quick-add');
    hostReady = window.customElements.whenDefined('quick-add-modal').then(buildHost);
    return hostReady;
  }

  function openQuickView(url, opener) {
    if (!url) return;
    if (window.CMQuick) window.CMQuick.get(url); // warm while the script loads

    // The panel is a corner card; leaving the bag open over it would bury it.
    var drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.classList.contains('active') && typeof drawer.close === 'function') {
      drawer.close();
    }

    var settled = false;
    var giveUp = window.setTimeout(function () {
      if (settled) return;
      settled = true;
      window.location.href = url; // the panel never arrived; the page always works
    }, 5000);

    ensureQuickView().then(function (host) {
      if (settled) return;
      settled = true;
      window.clearTimeout(giveUp);
      if (!host) {
        window.location.href = url;
        return;
      }
      opener.setAttribute('data-product-url', url);
      host.show(opener);
    });
  }

  function onOpenerClick(event) {
    if (event.defaultPrevented || event.button > 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var el = event.target && event.target.closest ? event.target.closest('[data-cm-qv-open]') : null;
    if (!el) return;

    var url = el.getAttribute('data-cm-qv-open');
    if (!url || url === 'true' || url === 'data-cm-qv-open') {
      url = el.getAttribute('href') || el.getAttribute('data-product-url') || '';
    }
    if (!url || url.charAt(0) === '#') return;

    event.preventDefault();
    openQuickView(url, el);
  }

  window.CMQuickView = { open: openQuickView };

  /* ------------------------------------------------- quick add: hit feedback */
  var lastVariantId = null;

  /* v2.1 item 3. Quick add now adds for every product, including the ones with
     Size and Colour, so a one-click add has to say which variant it took and
     where to change it. `intent` is recorded on the click and only becomes a
     `note` if that same variant really landed in the cart — an add made from
     the quick view is a deliberate choice and gets no note. */
  var intent = null;
  var note = null;

  function rememberIntent(event) {
    var button = event.target && event.target.closest ? event.target.closest('.cm-act--add[data-cm-multi]') : null;
    if (!button) return;
    var form = button.closest('form');
    var input = form ? form.querySelector('input.product-variant-id') : null;
    intent = {
      id: input ? String(input.value) : null,
      label: button.getAttribute('data-cm-multi') || '',
      url: button.getAttribute('data-cm-product-url') || '',
      card: button.closest('.card-wrapper, .cm-vitem'),
      at: Date.now(),
    };
  }

  function noteText(target, label) {
    var strong = document.createElement('strong');
    strong.textContent = label;
    target.appendChild(document.createTextNode('Added '));
    target.appendChild(strong);
    target.appendChild(document.createTextNode(' — change size or color in your bag'));
  }

  function showToast(info) {
    if (!info.card) return;
    var old = info.card.querySelector('.cm-added-toast');
    if (old) old.remove();

    // Over the photo, where the action bar was, rather than at the very bottom
    // of the card — the bottom of the card is the price row.
    var bar = info.card.querySelector('.cm-actions');
    var mount = (bar && bar.parentElement) || info.card;

    var toast = document.createElement('p');
    toast.className = 'cm-added-toast';
    toast.setAttribute('role', 'status');
    noteText(toast, info.label);
    mount.appendChild(toast);

    window.requestAnimationFrame(function () {
      toast.classList.add('cm-added-toast--in');
    });
    window.setTimeout(function () {
      toast.classList.remove('cm-added-toast--in');
      window.setTimeout(function () {
        toast.remove();
      }, 300);
    }, 4600);
  }

  function flashButton(button) {
    if (!button) return;
    button.classList.add('cm-act--done');
    window.setTimeout(function () {
      button.classList.remove('cm-act--done');
    }, 1400);
  }

  function onCartUpdate(payload) {
    if (!payload) return;
    lastVariantId = payload.productVariantId ? String(payload.productVariantId) : null;

    if (payload.source === 'product-form' && lastVariantId) {
      var input = document.querySelector(
        '.cm-actions__form input.product-variant-id[value="' + lastVariantId + '"]'
      );
      var slot = input ? input.closest('.cm-actions__slot') : null;
      if (slot) flashButton(slot.querySelector('.cm-act--add'));
    }

    note = intent && intent.id === lastVariantId && Date.now() - intent.at < 15000 ? intent : null;
    intent = null;
    if (note) showToast(note);

    // The drawer re-renders straight after this; decorateDrawer runs from the
    // observer below once the new markup is in the DOM.
  }

  /* --------------------------------------------------------- the cart drawer */
  /* The drawer's own markup belongs to the cart-drawer files; this only adds
     one line to the row that was just added, and only after a one-click add of
     a product that has options. The row can be a <tr> (Spotlight's table) or a
     plain element, so the note goes into a cell rather than beside the row. */
  function addDrawerNote(row) {
    if (!note || !note.label || row.querySelector('.cm-added-note')) return;

    var host = row.querySelector('.cart-item__details');
    if (!host) {
      var name = row.querySelector('.cart-item__name');
      if (name) host = name.parentElement;
    }
    if (!host) {
      var cells = row.querySelectorAll('td');
      host = cells.length > 1 ? cells[1] : null;
    }
    if (!host || host.tagName === 'TR') return;

    var el = document.createElement('div');
    el.className = 'cm-added-note';
    noteText(el, note.label);
    host.appendChild(el);
  }

  function decorateDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    var items = drawer.querySelectorAll('.cart-item');
    for (var i = 0; i < items.length; i += 1) {
      items[i].style.setProperty('--cm-i', String(i));
    }

    if (!lastVariantId) return;
    var input = drawer.querySelector('input[data-quantity-variant-id="' + lastVariantId + '"]');
    var row = input ? input.closest('.cart-item') : null;
    if (!row) return;

    addDrawerNote(row);

    if (row.classList.contains('cm-cart-item--added')) return;
    row.classList.add('cm-cart-item--added');
    window.setTimeout(function () {
      row.classList.remove('cm-cart-item--added');
      lastVariantId = null;
    }, 2200);
  }

  function watchDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof MutationObserver === 'undefined') return;

    var pending = null;
    var observer = new MutationObserver(function () {
      window.clearTimeout(pending);
      pending = window.setTimeout(decorateDrawer, 30);
    });

    observer.observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    decorateDrawer();
  }

  /* ============================================ v2.2 item 4 — the water sheen
     "For the product cards I wanted water animations — when I hover over it
     the bottom colour of the title of the product card should be a cool water
     animation, smooth and simple, and it follows the mouse."

     All this does is publish the pointer's position inside the hovered card as
     two 0–1 numbers, --cm-mx and --cm-my. cm-cards.css §8b turns them into the
     radial highlight on the tinted panel and the ±10px lean on the wave, and
     owns every duration; nothing here reads or writes a style that could be
     mistaken for design.

     Cheap by construction, because a collection page can hold thirty cards:

       one listener      a single passive `pointermove` on the document, not
                         one pair per card. Cards that facets.js re-renders are
                         covered without rebinding anything.
       one frame         one requestAnimationFrame for the whole page, not one
                         per card — only the card under the pointer can be
                         moving, so a second would have nothing to do.
       one measurement   the card's box is read when the pointer enters it and
                         re-read only after a scroll or a resize, so the
                         per-frame work is two setProperty calls and no layout.

     A card the pointer has left is handed back to CSS: the two properties are
     removed (so they fall back to the registered 0.5 initial value, i.e. the
     middle of the card) under a class that stretches the easing to 400ms.
     ------------------------------------------------------------------------ */
  var sheen = (function () {
    var CARDS = '.card-wrapper, .cm-vitem';
    var RETURN_MS = 420; // §8b eases back over 400ms; this outlasts it by a frame
    var hoverMQ = null;
    var reduceMQ = null;

    var card = null; // the card being tracked, or null
    var box = null; // its cached bounding rect
    var stale = false; // the rect needs re-reading (the page scrolled or resized)
    var px = 0;
    var py = 0;
    var frame = 0;
    /* One timer per card rather than one shared: a pointer crossing card A
       into card B releases A and engages B in the same event, and a single
       shared handle would have B's engage cancel A's return and strand the
       class on A. Weak, so a grid facets.js throws away is not held here. */
    var settling = typeof WeakMap === 'function' ? new WeakMap() : null;

    /* Read every time rather than once at start-up: both queries can flip
       while the page is open — a preference toggled in the OS, or a laptop
       that gains a mouse. */
    function allowed() {
      return !!hoverMQ && hoverMQ.matches && !(reduceMQ && reduceMQ.matches);
    }

    function paint() {
      frame = 0;
      if (!card) return;
      if (!box || stale) {
        box = card.getBoundingClientRect();
        stale = false;
      }
      if (!box.width || !box.height) return;

      var x = (px - box.left) / box.width;
      var y = (py - box.top) / box.height;
      // A pointer over the action bar or a badge is still "on the card", but
      // sub-pixel rounding can put it a hair outside the box.
      if (x < 0) x = 0;
      else if (x > 1) x = 1;
      if (y < 0) y = 0;
      else if (y > 1) y = 1;

      card.style.setProperty('--cm-mx', x.toFixed(3));
      card.style.setProperty('--cm-my', y.toFixed(3));
    }

    function settled(el) {
      if (!settling) return;
      window.clearTimeout(settling.get(el));
      settling.delete(el);
    }

    function release() {
      if (!card) return;
      var left = card;
      card = null;
      box = null;

      // Removing the two properties hands the card back to the registered
      // 0.5 initial value — the middle of the card — and the class makes that
      // return take 400ms instead of the 120ms of ordinary tracking.
      left.classList.add('cm-sheen-out');
      left.style.removeProperty('--cm-mx');
      left.style.removeProperty('--cm-my');

      if (!settling) return;
      settled(left);
      settling.set(
        left,
        window.setTimeout(function () {
          left.classList.remove('cm-sheen-out');
          settling.delete(left);
        }, RETURN_MS)
      );
    }

    function engage(next) {
      settled(next);
      next.classList.remove('cm-sheen-out');
      card = next;
      box = next.getBoundingClientRect();
      stale = false;
    }

    function onMove(event) {
      // A finger or a stylus gets the wave's drift and nothing else.
      if (event.pointerType && event.pointerType !== 'mouse') return;
      if (!allowed()) {
        release();
        return;
      }

      var next = event.target && event.target.closest ? event.target.closest(CARDS) : null;
      if (next !== card) {
        release();
        if (!next) return;
        engage(next);
      }

      px = event.clientX;
      py = event.clientY;
      if (!frame) frame = window.requestAnimationFrame(paint);
    }

    function invalidate() {
      stale = true;
    }

    return function start() {
      if (!window.matchMedia || typeof window.requestAnimationFrame !== 'function') return;
      hoverMQ = window.matchMedia('(hover: hover) and (pointer: fine)');
      reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');

      document.addEventListener('pointermove', onMove, { passive: true });
      // The pointer leaving the window, or the OS taking it away, never sends
      // a move event over the page background that would end the hover.
      document.addEventListener('pointerleave', release, { passive: true });
      document.addEventListener('pointercancel', release, { passive: true });
      // The rect is in viewport coordinates, so any scroll invalidates it —
      // including the variety rail's own horizontal one, which is why this
      // listens in the capture phase rather than on window (scroll events from
      // an element do not bubble). Both handlers are passive and only set a
      // flag; the re-read happens inside the next rAF.
      document.addEventListener('scroll', invalidate, { passive: true, capture: true });
      window.addEventListener('resize', invalidate, { passive: true });
    };
  })();

  /* --------------------------------------------------------------- start up */
  function init() {
    ensureCardScripts();
    watchIntent();
    watchDrawer();
    sheen();
    document.addEventListener('click', rememberIntent);
    document.addEventListener('click', onOpenerClick);

    // constants.js declares PUB_SUB_EVENTS with `const`, so it lives in the
    // global lexical scope and never appears on `window` — it has to be read
    // as a bare identifier, behind a typeof guard.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
